import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import TreeItem, { TreeItemContentProps, TreeItemProps, useTreeItem } from "@mui/lab/TreeItem";
import TreeView, { SingleSelectTreeViewProps } from "@mui/lab/TreeView";
import { styled } from "@mui/material/styles";
import clsx from "clsx";
import Text from "designSystem/components/text/Text";
import { Box, Checkbox, OverflowText } from "designSystem/index";
import { grey } from "designSystem/styles/color";
import { disabledCursorStyling } from "designSystem/styles/style-functions";
import { action, computed, makeObservable, observable } from "mobx";
import { observer } from "mobx-react-lite";
import React, { FunctionComponent, ReactNode, forwardRef } from "react";

const CountText = styled(Text)(({ theme }) => ({
	color: grey["A100"],
	marginLeft: 8,
	minWidth: "fit-content",
}));

export class CheckboxTreeNode {
	/* Should either have both onChange and state defined or children defined */

	/* Name to be displayed on the checkbox item */
	name: string;

	/* Children for the checkbox item */
	children?: CheckboxTreeNode[];

	/* If this is true, the item should be disabled */
	private readonly _disabled?: boolean;

	/* The node's parent node */
	parent?: CheckboxTreeNode;

	/* If this is included, the row will use this component as its label */
	label?: ReactNode;

	/* Whether the item's checkbox is checked */
	@observable state?: boolean;

	/* The store containing details about the tree */
	store?: CheckboxTreeStore;

	/* a unique id for the item */
	uniqueId: string;

	/* A string describing how many of the item's children are selected */
	get countText(): string {
		const { allLeafNodes, children, selectedNodes } = this;

		return children ? `${selectedNodes.length} of ${allLeafNodes.length}` : "";
	}

	/* Whether the child should display its value as checked.  Uses state or children */
	get displayChecked(): boolean {
		if (!this.children) {
			return true;
		} else {
			return this.selectedNodes.length > 0;
		}
	}

	get disabled(): boolean {
		if (!this.children) {
			return this._disabled;
		} else {
			return this.children.filter((node) => !node._disabled).length == 0;
		}
	}

	get hasChildren(): boolean {
		return !!this.children?.length;
	}

	/* If the checkbox has children, use this to determine whether they have mixed truthiness states */
	@computed get indeterminate(): boolean {
		let indeterminate = false;
		let seenFalse = false;
		let seenTrue = false;

		this.children?.forEach((child) => {
			if (child.indeterminate) {
				indeterminate = true;
			} else if (child.state) {
				seenTrue = true;
			} else {
				seenFalse = true;
			}
		});

		return indeterminate || (seenTrue && seenFalse);
	}

	/* Change the node's state and all enabled children's states to the given value */
	@action changeAllChildren = (value: boolean): void => {
		this.state = value;

		if (this.children) {
			this.children.forEach((child) => {
				if (!child._disabled) {
					child.changeAllChildren(value);
				}
			});
		}

		if (!this.indeterminate) {
			this.store?.onChangeTreeNode?.(this);
		}
	};

	/* Call onChange function on all parent nodes of the node */
	onChangeAllParents = (): void => {
		const { parent } = this;

		if (parent) {
			parent.store?.onChangeTreeNode?.(parent);
			parent.onChangeAllParents();
		}
	};

	@action onChange = (event: any, value: boolean): void => {
		this.changeAllChildren(value);
		this.onChangeAllParents();
		this.store.onChangeTree?.();
	};

	/* Get all nodes with no children */
	@computed get allLeafNodes(): CheckboxTreeNode[] {
		const allLeafNodes: CheckboxTreeNode[] = [];

		this.getAllLeafNodes(this.children, allLeafNodes);
		return allLeafNodes;
	}

	private getAllLeafNodes(children: CheckboxTreeNode[], allLeafNodes: CheckboxTreeNode[]): void {
		children?.forEach((child) => {
			if (child.children) {
				this.getAllLeafNodes(child.children, allLeafNodes);
			} else {
				allLeafNodes.push(child);
			}
		});
	}

	@computed get selectedNodes(): CheckboxTreeNode[] {
		const selectedNodes: CheckboxTreeNode[] = [];

		this.getSelectedNodes(this.children, selectedNodes);
		return selectedNodes;
	}

	private getSelectedNodes(children: CheckboxTreeNode[], selectedNodes: CheckboxTreeNode[]): void {
		children?.forEach((child) => {
			if (child.children) {
				this.getSelectedNodes(child.children, selectedNodes);
			} else if (child.state) {
				selectedNodes.push(child);
			}
		});
	}

	addChild(node: CheckboxTreeNode) {
		this.children = this.children || [];

		this.children.push(node);
		node.store = this.store;
		node.parent = this;
	}

	constructor(store: CheckboxTreeStore | undefined, uniqueId: string, name: string, state: boolean, disabled?: boolean, parent?: CheckboxTreeNode) {
		makeObservable(this);
		this._disabled = disabled;
		this.uniqueId = uniqueId;
		this.state = state;
		this.name = name;
		this.store = store;
		this.parent = parent;
	}

	static createCheckboxTreeNode (treeNode: CheckboxTreeNode, keepStore: boolean = true): CheckboxTreeNode {
		return new CheckboxTreeNode(keepStore ? treeNode.store : null, treeNode.uniqueId, treeNode.name, treeNode.state, treeNode._disabled, treeNode.parent);
	}
}

export class CheckboxTreeStore {
	children: CheckboxTreeNode[];

	/* Callback that will be called when the tree is changed */
	onChangeTree?: () => void;

	/* Callback that will be called when an individual node is changed */
	onChangeTreeNode?: (checkboxTreeNode: CheckboxTreeNode) => void;

	constructor(children?: CheckboxTreeNode[], onChangeTree?: () => void, onChangeTreeNode?: (node: CheckboxTreeNode) => void) {
		makeObservable(this);
		this.assignStoreToChildren(children);
		this.children = children;
		this.onChangeTree = onChangeTree;
		this.onChangeTreeNode = onChangeTreeNode;
	}

	assignStoreToChildren(children?: CheckboxTreeNode[]) {
		children?.forEach((child) => {
			child.store = this;
			this.assignStoreToChildren(child.children);
		});
	}

	@action createNode(uniqueId: string, name: string, state: boolean, parent?: CheckboxTreeNode, disabled?: boolean): CheckboxTreeNode {
		const node = new CheckboxTreeNode(this, uniqueId, name, state, disabled, parent);

		if (parent) {
			parent.children ||= [];

			parent.children.push(node);
		} else {
			this.children = this.children || [];
			this.children.push(node);
		}

		return node;
	}

	@action insertNode(node: CheckboxTreeNode, parent?: CheckboxTreeNode): void {
		if (parent) {
			parent.children ||= [];

			parent.children.push(node);
			node.parent = parent;
		} else {
			this.children = this.children || [];

			this.children.push(node);
			node.parent = undefined;
		}

		node.store = this;
		this.assignStoreToChildren(node.children);
	}
}



interface CheckboxTreeItemProperties {
	/* Whether the checkbox for the item should display as indeterminate */
	checkIndeterminate?: boolean;

	/* If this is true, the item should be disabled */
	disabled?: boolean;

	/* Whether the item's checkbox is checked */
	state: boolean;

	/* Callback for clicking the checkbox */
	onCheck: (event: any, value: boolean) => void;
}

interface CheckboxTreeItemContentProps extends TreeItemContentProps, CheckboxTreeItemProperties {
	/** If the count of the item's children should be displayed, use this component */
	countComponent?: ReactNode;
}

const CheckboxTreeItemContent = forwardRef(function CustomContent(props: CheckboxTreeItemContentProps, ref: any) {
	const {
		checkIndeterminate,
		classes,
		className,
		countComponent,
		disabled,
		displayIcon,
		expansionIcon,
		icon: iconProp,
		label,
		nodeId,
		onCheck,
		state,
	} = props;

	const {
		expanded,
		focused,
		selected,
		handleExpansion,
		preventSelection,
	} = useTreeItem(nodeId);

	const icon = iconProp || expansionIcon || displayIcon;
	let displayLabel = typeof label === "string" ? <OverflowText text={label} /> : label;
	if (countComponent) {
		displayLabel = (
			<>
				{displayLabel}
				{countComponent}
			</>
		);
	}

	const CheckboxLabel = (
		<>
			<Box
				className={classes.label}
			>
				{displayLabel}
			</Box>
		</>
	);

	const useClassName = clsx(className, classes.root, {
		[classes.expanded]: expanded,
		[classes.selected]: selected,
		[classes.focused]: focused,
		[classes.disabled]: disabled,
	});

	const cursorStyling = disabledCursorStyling(disabled);

	return (
		<div
			className={useClassName}
			onMouseDown={preventSelection}
			ref={ref}
		>
			<div className={classes.iconContainer} onClick={handleExpansion}>
				{icon}
			</div>
			<Checkbox
				checked={state}
				disabled={disabled}
				indeterminate={checkIndeterminate ? true : undefined}
				labelProps={{
					label: CheckboxLabel,
					sx: {
						cursor: "auto",
						textAlign: "start",
						flexGrow: 1,
						"&>.MuiFormControlLabel-label": {
							cursor: "auto",
							flexGrow: 1,
						},
						"& .MuiTreeItem-label *": {
							cursor: cursorStyling,
						},
					},
				}}
				onChange={onCheck}
			/>
		</div>
	);
});

export interface CheckboxTreeItemProps extends TreeItemProps, CheckboxTreeItemProperties {
	/** If the count of the item's children should be displayed, use this component */
	countComponent?: ReactNode;
}

const CheckboxTreeItem: FunctionComponent<CheckboxTreeItemProps> = (props) => {
	const {
		checkIndeterminate,
		countComponent,
		disabled,
		onCheck,
		state,
		...otherProps
	} = props;
	return (
		<TreeItem
			ContentComponent={CheckboxTreeItemContent}
			/* Any cast is necessary because ContentProps needs properties that aren't on the default type */
			ContentProps={{
				checkIndeterminate,
				countComponent,
				disabled,
				onCheck,
				state,
			} as any}
			{...otherProps}
		/>
	);
};

export interface RenderTreeContentsProps {
	/* Spacing between the children */
	branchSpacing?: number | string;

	/* Whether the tree should be disabled */
	disabled?: boolean;

	/* Whether a count should be displayed for each root */
	displayCounts?: boolean;

	child: CheckboxTreeNode;
}

const RenderTreeContents: FunctionComponent<RenderTreeContentsProps> = observer((props) => {
	const {
		branchSpacing,
		disabled,
		displayCounts,
		child,
	} = props;
	const {
		children,
		countText,
		disabled: childDisabled,
		displayChecked,
		indeterminate,
		label,
		name,
		onChange,
		state,
		uniqueId,
	} = child;

	if (!children) {
		return (
			<CheckboxTreeItem
				disabled={disabled || childDisabled}
				key={uniqueId}
				label={label || name}
				nodeId={uniqueId}
				onCheck={onChange}
				state={state}
				sx={{
					paddingBottom: branchSpacing === undefined ? 1 : branchSpacing,
				}}
			/>
		);
	} else {
		let countComponent;
		if (displayCounts) {
			countComponent = <CountText text={countText} />;
		}

		return (
			<CheckboxTreeItem
				checkIndeterminate={indeterminate}
				countComponent={countComponent}
				disabled={disabled || childDisabled}
				key={uniqueId}
				label={label || name}
				nodeId={uniqueId}
				onCheck={onChange}
				state={displayChecked}
			>
				{children.map((child) => (
					<RenderTreeContents
						branchSpacing={branchSpacing}
						child={child}
						disabled={disabled}
						key={child.uniqueId}
					/>
				))}
			</CheckboxTreeItem>
		);
	}
});

export interface CheckboxTreeProps extends Omit<SingleSelectTreeViewProps, "children"> {
	/* Spacing between the children */
	branchSpacing?: number | string;

	/* Whether the tree should be disabled */
	disabled?: boolean;

	/* Whether a count should be displayed for each root */
	displayCounts?: boolean;

	/* The store containing children to be displayed in the tree */
	store: CheckboxTreeStore;
}

const CheckboxTree: FunctionComponent<CheckboxTreeProps> = (props) => {
	const { branchSpacing, store, disabled, displayCounts, ...otherProps } = props;
	const { children } = store;

	return (
		<TreeView
			defaultCollapseIcon={<KeyboardArrowDownIcon />}
			defaultExpandIcon={<ChevronRightIcon />}
			sx={{
				flexGrow: 1,
				overflowY: "auto",
			}}
			{...otherProps}
		>
			{children.map((child) => (
				<RenderTreeContents
					branchSpacing={branchSpacing}
					child={child}
					disabled={disabled}
					displayCounts={displayCounts}
					key={child.uniqueId}
				/>
			))}
		</TreeView>
	);
};

export default CheckboxTree;